import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { FeedbackProvider } from "@/components/ui/feedback";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Layout } from "@/components/Layout";
import { PublicLayout } from "@/components/PublicLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ProtectedAdminRoute } from "@/components/ProtectedAdminRoute";

// Pages
import LandingPage from "@/pages/LandingPage";
import DashboardPage from "@/pages/DashboardPage";
import NetworksPage from "@/pages/NetworksPage";
import NetworkPage from "@/pages/NetworkPage";
import NetworkSettingsPage from "@/pages/NetworkSettingsPage";
import AddNetworkPage from "@/pages/AddNetworkPage";
import ValidationPage from "@/pages/ValidationPage";
import ValidationsPage from "@/pages/ValidationsPage";
import JobPage from "@/pages/JobPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import DeviceAuthPage from "@/pages/DeviceAuthPage";
import SettingsPage from "@/pages/SettingsPage";
import DocsPage from "@/pages/docs/DocsPage";
import AgreementPage from "@/pages/AgreementPage";

// Admin pages
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminUserDetailPage from "@/pages/admin/AdminUserDetailPage";
import AdminBillingPage from "@/pages/admin/AdminBillingPage";
import AdminBetaSignupsPage from "@/pages/admin/AdminBetaSignupsPage";
import AdminAuditLogsPage from "@/pages/admin/AdminAuditLogsPage";

function App() {
  return (
    <ThemeProvider>
      <FeedbackProvider>
        <AuthProvider>
          <div className="min-h-screen bg-background">
            <Routes>
              {/* Public routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/device" element={<DeviceAuthPage />} />
              </Route>

              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="/agreement" element={<AgreementPage />} />
                <Route element={<Layout />}>
                  <Route path="/overview" element={<DashboardPage />} />
                  <Route
                    path="/dashboard"
                    element={<Navigate to="/overview" replace />}
                  />
                  <Route path="/networks" element={<NetworksPage />} />
                  <Route path="/networks/add" element={<AddNetworkPage />} />
                  <Route path="/networks/:id" element={<NetworkPage />} />
                  <Route path="/validations" element={<ValidationsPage />} />
                  <Route
                    path="/networks/:id/settings"
                    element={<NetworkSettingsPage />}
                  />
                  <Route path="/runs" element={<Navigate to="/validations" replace />} />
                  <Route path="/jobs/:id" element={<JobPage />} />
                  <Route path="/validations/:id" element={<ValidationPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/docs" element={<DocsPage />} />
                  <Route path="/docs/:slug" element={<DocsPage />} />
                  <Route path="/docs/*" element={<DocsPage />} />
                </Route>
              </Route>

              {/* Admin routes */}
              <Route element={<ProtectedAdminRoute />}>
                <Route element={<Layout />}>
                  <Route path="/admin" element={<AdminDashboardPage />} />
                  <Route path="/admin/users" element={<AdminUsersPage />} />
                  <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
                  <Route path="/admin/billing" element={<AdminBillingPage />} />
                  <Route path="/admin/beta-signups" element={<AdminBetaSignupsPage />} />
                  <Route path="/admin/audit-logs" element={<AdminAuditLogsPage />} />
                </Route>
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster />
          </div>
        </AuthProvider>
      </FeedbackProvider>
    </ThemeProvider>
  );
}

export default App;
